import { isEqual } from 'es-toolkit';

import {
  AnalysisSet,
  AnalysisSettings,
  ComponentDesignTimestamps,
  DesignStandard,
  FeaturePatternTrussSettings,
  LoadSettings,
  MaterialSettingsPair,
  OrganizationSettings,
  ProfileDefaults,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getChildrenFinder, getComponentDesignDescendantGuids } from '@paragon/layout-models';
import { getLoggedInUser } from '@paragon/login-tools';
import { assertExhaustiveSwitch, forceArray } from '@paragon/utilities';

import { SaveOptions } from '../../components/interface/subpanels/settings/interfaces';
import { SaveAction } from '../../components/interface/subpanels/settings/SettingsSaveButton';
import { getProjectLayoutModel } from '../selectors/getProjectLayoutModel';
import { isLinkUser } from '../selectors/isLinkUser';
import { getAnalysisSettings, getLoadSettings } from '../selectors/settingsDatabaseSelectors';
import { applicationOrganizationSettingsLoaded } from '../slices/applicationSlice';
import { setComponentDesignTimestamps } from '../slices/componentDesignTimestampsSlice';
import {
  setAnalysisSettings,
  setAnalysisSettingsFromObject,
  setFeaturePatternSettings,
  setLoadSettings,
  setLoadSettingsFromObject,
  setMaterialSettings,
  setProfileDefaults,
} from '../slices/settingsDatabaseSlice';
import { loadAnalysisSettings } from './loadActions';
import { AsyncThunkActionReturn, AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export function saveAnalysisSettings(settings: AnalysisSettings, options: SaveOptions) {
  switch (options.action) {
    case SaveAction.DefaultsForHierarchy:
      return saveDefaultAnalysisSettingsInProject(options.projectGuid, options.modelGuid, settings, true);

    case SaveAction.DefaultsForSingleLevel:
      return saveDefaultAnalysisSettingsInProject(options.projectGuid, options.modelGuid, settings, false);

    case SaveAction.ActualForHierarchy:
      return pushDownAnalysisSettings(options.projectGuid, options.modelGuid, settings);

    case SaveAction.ActualForSingleComponent:
      return saveComponentAnalysisSettings(options.modelGuid, settings);

    default:
      assertExhaustiveSwitch(options, 'Unrecognized save action.');
  }
}

export function saveLoadSettings(settings: LoadSettings, options: SaveOptions) {
  switch (options.action) {
    case SaveAction.DefaultsForHierarchy:
      return saveDefaultLoadSettingsInProject(options.projectGuid, options.modelGuid, settings, true);

    case SaveAction.DefaultsForSingleLevel:
      return saveDefaultLoadSettingsInProject(options.projectGuid, options.modelGuid, settings, false);

    case SaveAction.ActualForHierarchy:
      return pushDownLoadSettings(options.projectGuid, options.modelGuid, settings);

    case SaveAction.ActualForSingleComponent:
      return saveComponentLoadSettings(options.modelGuid, settings);

    default:
      assertExhaustiveSwitch(options, 'Unrecognized save action.');
  }
}

export function saveMaterialSettings(settings: MaterialSettingsPair, options: SaveOptions) {
  switch (options.action) {
    case SaveAction.DefaultsForHierarchy:
      return saveDefaultMaterialSettingsInProject(options.projectGuid, options.modelGuid, settings, true);

    case SaveAction.DefaultsForSingleLevel:
      return saveDefaultMaterialSettingsInProject(options.projectGuid, options.modelGuid, settings, false);

    case SaveAction.ActualForHierarchy:
      return pushDownMaterialSettings(options.projectGuid, options.modelGuid, settings);

    case SaveAction.ActualForSingleComponent:
      return saveComponentMaterialSettings(options.modelGuid, settings);

    default:
      assertExhaustiveSwitch(options, 'Unrecognized save action.');
  }
}

export function saveFeaturePatternSettings(settings: FeaturePatternTrussSettings, options: SaveOptions) {
  switch (options.action) {
    case SaveAction.DefaultsForHierarchy:
      return saveDefaultFeaturePatternSettingsInProject(options.projectGuid, options.modelGuid, settings, true);

    case SaveAction.DefaultsForSingleLevel:
      return saveDefaultFeaturePatternSettingsInProject(options.projectGuid, options.modelGuid, settings, false);

    case SaveAction.ActualForHierarchy:
    case SaveAction.ActualForSingleComponent:
      throw new Error(
        'Editing actual feature pattern settings is not supported.  You should probably be using a component macro?',
      );
  }
}

export function saveProfileDefaults(settings: ProfileDefaults, options: SaveOptions) {
  switch (options.action) {
    case SaveAction.DefaultsForHierarchy:
      return saveDefaultProfileDefaultsInProject(options.projectGuid, options.modelGuid, settings, true);

    case SaveAction.DefaultsForSingleLevel:
      return saveDefaultProfileDefaultsInProject(options.projectGuid, options.modelGuid, settings, false);

    case SaveAction.ActualForHierarchy:
    case SaveAction.ActualForSingleComponent:
      throw new Error('Editing actual profile defaults is a contradiction. This is not supported.');
  }
}

export const patchDesignStandard =
  (newDesignStandard: DesignStandard, options: SaveOptions): AsyncThunkActionVoid =>
  async (dispatch) => {
    const analysisSettings = await dispatch(loadAnalysisSettings(options.modelGuid));
    await dispatch(saveAnalysisSettings({ ...analysisSettings, designStandard: newDesignStandard }, options));
  };

export const applyComponentSettingsFromAnalysisSet =
  ({ componentDesign, loadSettings, analysisSettings }: AnalysisSet): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const settingsPromises = [];

    const oldLoadSettings = getLoadSettings(state, componentDesign.guid, { ignoreAnalysisSetPreviews: true });
    if (!isEqual(oldLoadSettings, loadSettings)) {
      settingsPromises.push(dispatch(saveComponentLoadSettings(componentDesign.guid, loadSettings)));
    }

    const oldAnalysisSettings = getAnalysisSettings(state, componentDesign.guid, { ignoreAnalysisSetPreviews: true });
    if (!isEqual(oldAnalysisSettings, analysisSettings)) {
      settingsPromises.push(dispatch(saveComponentAnalysisSettings(componentDesign.guid, analysisSettings)));
    }

    if (settingsPromises.length) {
      await Promise.all(settingsPromises);
    }
  };

export const saveComponentAnalysisSettings =
  (modelGuid: string, analysisSettings: AnalysisSettings): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const activeProject = getProjectLayoutModel(getState());

    // If the component exists in a project we want to use the layout model routes to make sure the building code
    // stays in-sync with the project.
    const timestamps = activeProject
      ? await designService.settings.postSetModelAnalysisSettingsInProject(
          activeProject.guid,
          modelGuid,
          analysisSettings,
          false,
        )
      : await designService.settings.postSetComponentAnalysisSettings(modelGuid, analysisSettings);

    dispatch(setAnalysisSettingsForComponentDesigns(modelGuid, analysisSettings));
    dispatch(setComponentDesignTimestamps({ timestamps }));
  };

export const saveComponentLoadSettings =
  (modelGuid: string, loadSettings: LoadSettings): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const activeProject = getProjectLayoutModel(getState());

    // If the component exists in a project we want to use the layout model routes to make sure the building code
    // stays in-sync with the project.
    const timestamps = activeProject
      ? await designService.settings.postSetModelLoadSettingsInProject(
          activeProject.guid,
          modelGuid,
          loadSettings,
          false,
        )
      : await designService.settings.postSetComponentLoadSettings(modelGuid, loadSettings);

    dispatch(setLoadSettingsForComponentDesigns(modelGuid, loadSettings));
    dispatch(setComponentDesignTimestamps({ timestamps }));
  };

export const saveComponentMaterialSettings =
  (modelGuid: string, materialSettings: MaterialSettingsPair): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    let timestamps: ComponentDesignTimestamps;

    const definitionType = getState().componentDesigns.data[modelGuid]?.component.definitionType;
    switch (definitionType) {
      case null:
      case 'Wall':
        throw new Error('Component design not found or definition type not supported.');

      case 'FeaturePattern':
        if (!materialSettings.featurePatternMaterialSettings)
          throw new Error('Feature pattern material settings not provided.');

        timestamps = await designService.settings.postSetComponentFeaturePatternMaterialSettings(
          modelGuid,
          materialSettings.featurePatternMaterialSettings,
        );
        break;

      case 'Profile':
        if (!materialSettings.profileMaterialSettings) throw new Error('Profile material settings not provided.');

        timestamps = await designService.settings.postSetComponentProfileMaterialSettings(
          modelGuid,
          materialSettings.profileMaterialSettings,
        );
        break;

      default:
        assertExhaustiveSwitch(definitionType);
    }

    dispatch(setMaterialSettingsForComponentDesigns(modelGuid, materialSettings));
    dispatch(setComponentDesignTimestamps({ timestamps }));
  };

export const saveDefaultAnalysisSettingsInProject =
  (
    projectGuid: string,
    modelGuid: string,
    analysisSettings: AnalysisSettings,
    setForEntireHierarchy: boolean,
  ): AsyncThunkActionVoid =>
  async (dispatch) => {
    await designService.settings.postSetModelAnalysisSettingsInProject(
      projectGuid,
      modelGuid,
      analysisSettings,
      setForEntireHierarchy,
    );
    dispatch(setAnalysisSettingsForComponentDesigns(modelGuid, analysisSettings));
  };

export const saveDefaultLoadSettingsInProject =
  (
    projectGuid: string,
    modelGuid: string,
    loadSettings: LoadSettings,
    setForEntireHierarchy: boolean,
  ): AsyncThunkActionVoid =>
  async (dispatch) => {
    await designService.settings.postSetModelLoadSettingsInProject(
      projectGuid,
      modelGuid,
      loadSettings,
      setForEntireHierarchy,
    );
    dispatch(setLoadSettingsForComponentDesigns(modelGuid, loadSettings));
  };

export const saveDefaultMaterialSettingsInProject =
  (
    projectGuid: string,
    modelGuid: string,
    materialSettings: MaterialSettingsPair,
    setForEntireHierarchy: boolean,
  ): AsyncThunkActionVoid =>
  async (dispatch) => {
    await designService.settings.postSetModelMaterialSettingsInProject(
      projectGuid,
      modelGuid,
      materialSettings,
      setForEntireHierarchy,
    );
    dispatch(setMaterialSettingsForComponentDesigns(modelGuid, materialSettings));
  };

export const saveDefaultFeaturePatternSettingsInProject =
  (
    projectGuid: string,
    modelGuid: string,
    featurePatternSettings: FeaturePatternTrussSettings,
    setForEntireHierarchy: boolean,
  ): AsyncThunkActionVoid =>
  async (dispatch) => {
    await designService.settings.postSetModelFeaturePatternSettingsInProject(
      projectGuid,
      modelGuid,
      featurePatternSettings,
      setForEntireHierarchy,
    );
    dispatch(setFeaturePatternSettingsForComponentDesigns(modelGuid, featurePatternSettings));
  };

export const saveDefaultProfileDefaultsInProject =
  (
    projectGuid: string,
    modelGuid: string,
    profileDefaults: ProfileDefaults,
    setForEntireHierarchy: boolean,
  ): AsyncThunkActionVoid =>
  async (dispatch) => {
    await designService.settings.postSetModelProfileDefaultsInProject(
      projectGuid,
      modelGuid,
      profileDefaults,
      setForEntireHierarchy,
    );
    dispatch(setProfileDefaultsForComponentDesigns(modelGuid, profileDefaults));
  };

export const pushDownAnalysisSettings =
  (projectGuid: string, modelGuid: string, analysisSettings: AnalysisSettings): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    await designService.settings.postSetModelAnalysisSettingsInProject(projectGuid, modelGuid, analysisSettings, false);
    const componentDesignTimestamps = await designService.settings.postPushDownSettings(
      projectGuid,
      modelGuid,
      'analysisSettings',
      analysisSettings,
    );

    const componentDesignDescendants = getComponentDesignDescendantGuids(modelGuid, getChildrenFinder(getState()));
    dispatch(setAnalysisSettingsForComponentDesigns(componentDesignDescendants, analysisSettings));
    for (const timestamps of componentDesignTimestamps) {
      dispatch(setComponentDesignTimestamps({ timestamps }));
    }
  };

export const pushDownLoadSettings =
  (projectGuid: string, modelGuid: string, loadSettings: LoadSettings): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    await designService.settings.postSetModelLoadSettingsInProject(projectGuid, modelGuid, loadSettings, false);
    const componentDesignTimestamps = await designService.settings.postPushDownSettings(
      projectGuid,
      modelGuid,
      'loadSettings',
      loadSettings,
    );

    const componentDesignDescendants = getComponentDesignDescendantGuids(modelGuid, getChildrenFinder(getState()));
    dispatch(setLoadSettingsForComponentDesigns(componentDesignDescendants, loadSettings));
    for (const timestamps of componentDesignTimestamps) {
      dispatch(setComponentDesignTimestamps({ timestamps }));
    }
  };

export const pushDownMaterialSettings =
  (projectGuid: string, modelGuid: string, settings: MaterialSettingsPair): AsyncThunkActionVoid =>
  async (dispatch) => {
    await designService.settings.postSetModelMaterialSettingsInProject(projectGuid, modelGuid, settings, false);

    if (settings.profileMaterialSettings) {
      const profileTimestamps = await designService.settings.postPushDownSettings(
        projectGuid,
        modelGuid,
        'profileMaterialSettings',
        settings.profileMaterialSettings,
      );
      dispatch(setComponentDesignTimestamps({ timestamps: profileTimestamps }));
    }

    if (settings.featurePatternMaterialSettings) {
      const featurePatternTimestamps = await designService.settings.postPushDownSettings(
        projectGuid,
        modelGuid,
        'featurePatternMaterialSettings',
        settings.featurePatternMaterialSettings,
      );
      dispatch(setComponentDesignTimestamps({ timestamps: featurePatternTimestamps }));
    }
  };

export const setAnalysisSettingsForComponentDesigns =
  (modelGuids: string | string[], settings: AnalysisSettings): ThunkActionVoid =>
  (dispatch, getState) => {
    // Only settings for component designs are allowed to be stored in redux state.
    const componentDesignGuids = forceArray(modelGuids).filter((guid) => guid in getState().componentDesigns.data);
    if (componentDesignGuids.length === 0) return;

    dispatch(
      setAnalysisSettings({
        modelGuids: componentDesignGuids,
        analysisSettings: settings,
      }),
    );
  };

export const setLoadSettingsForComponentDesigns =
  (modelGuids: string | string[], settings: LoadSettings): ThunkActionVoid =>
  (dispatch, getState) => {
    // Only settings for component designs are allowed to be stored in redux state.
    const componentDesignGuids = forceArray(modelGuids).filter((guid) => guid in getState().componentDesigns.data);
    if (componentDesignGuids.length === 0) return;

    dispatch(
      setLoadSettings({
        modelGuids: componentDesignGuids,
        loadSettings: settings,
      }),
    );
  };

export const setLoadSettingsForComponentDesignsFromObject =
  (loadSettingsDictionary: { readonly [modelGuid: string]: LoadSettings }): ThunkActionVoid =>
  (dispatch, getState) => {
    // Only settings for component designs are allowed to be stored in redux state.
    const loadSettingsToSave = Object.fromEntries(
      Object.entries(loadSettingsDictionary).filter(([modelGuid]) => modelGuid in getState().componentDesigns.data),
    );
    if (Object.keys(loadSettingsToSave).length === 0) return;

    dispatch(setLoadSettingsFromObject(loadSettingsToSave));
  };

export const setAnalysisSettingsForComponentDesignsFromObject =
  (analysisSettingsDictionary: { readonly [modelGuid: string]: AnalysisSettings }): ThunkActionVoid =>
  (dispatch, getState) => {
    // Only settings for component designs are allowed to be stored in redux state.
    const analysisSettingsToSave = Object.fromEntries(
      Object.entries(analysisSettingsDictionary).filter(([modelGuid]) => modelGuid in getState().componentDesigns.data),
    );
    if (Object.keys(analysisSettingsToSave).length === 0) return;

    dispatch(setAnalysisSettingsFromObject(analysisSettingsToSave));
  };

export const setMaterialSettingsForComponentDesigns =
  (modelGuids: string | string[], settings: MaterialSettingsPair): ThunkActionVoid =>
  (dispatch, getState) => {
    // Only settings for component designs are allowed to be stored in redux state.
    const componentDesignGuids = forceArray(modelGuids).filter((guid) => guid in getState().componentDesigns.data);
    if (componentDesignGuids.length === 0) return;

    dispatch(
      setMaterialSettings({
        modelGuids: componentDesignGuids,
        materialSettings: settings,
      }),
    );
  };

export const setFeaturePatternSettingsForComponentDesigns =
  (modelGuid: string, settings: FeaturePatternTrussSettings): ThunkActionVoid =>
  (dispatch, getState) => {
    if (modelGuid in getState().componentDesigns.data) {
      throw new Error(
        'Feature pattern settings for actual components can not be stored in the settings database. This was a deliberate design decision.',
      );
    }

    dispatch(
      setFeaturePatternSettings({
        modelGuids: [modelGuid],
        featurePatternSettings: settings,
      }),
    );
  };

export const setProfileDefaultsForComponentDesigns =
  (modelGuid: string, settings: ProfileDefaults): ThunkActionVoid =>
  (dispatch, getState) => {
    if (modelGuid in getState().componentDesigns.data) {
      throw new Error(
        'Profile defaults for actual components can not be stored in the settings database. This was a deliberate design decision.',
      );
    }

    dispatch(
      setProfileDefaults({
        modelGuids: [modelGuid],
        profileDefaults: settings,
      }),
    );
  };

/**
 * Returns the organization's settings, fetching them if this session has not already. Startup asks for them but does
 * not block on the request, so callers that need them to be right cannot assume they have arrived.
 *
 * Anonymous and link users have no organization settings of their own, and we never ask the server for any on their
 * behalf. Both get `null`, and callers fall back to their own defaults.
 */
export const loadOrganizationSettingsIfNeeded =
  (): AsyncThunkActionReturn<OrganizationSettings | null> => async (dispatch, getState) => {
    const state = getState();
    if (!getLoggedInUser(state) || isLinkUser(state)) return null;

    const { organizationSettings, organizationSettingsLoaded } = state.application;
    if (organizationSettingsLoaded) return organizationSettings;

    const fetchedSettings = await designService.settings.getOrganizationSettings();
    dispatch(applicationOrganizationSettingsLoaded(fetchedSettings));

    return fetchedSettings;
  };
